import React, { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { useAccountQuery } from "features/auth/authApi";
import { useAppSelector } from "../store";
import MainLayout from "layout/main";
import Loading from "components/loading";
import SignUpWithReferral from "pages/signup-with-referral";
import FormLevel1 from "pages/form-level1";
import CorporateInfo1 from "pages/form-corporate1";
import CorporateInfo2 from "pages/form-corporate2";
import CorporateInfo3 from "pages/form-corporate3";
import CorporateInfo4 from "pages/form-corporate4";
import CorporateInfo5 from "pages/form-corporate5";
import CorporateInfo6 from "pages/form-corporate6";
import FormLevel1Declaration from "pages/form-level1-declaration";
import MobileVerification from "pages/mobile-verification";
import SignUpWithPassword from "pages/sign-up-with-password";
import SignUpWithPasswordIledgends from "pages/sign-up-with-password-iledgends";
import SuccessRegistration from "pages/success-registration";
import ConfirmEmail from "pages/confirm-email";
import UserMobileVerification from "pages/user-mobile-verification";
import SumSubVerification from "pages/sum-sub-verification";
import FileUploader from "pages/uploader";
import Transaction from "pages/transaction";

import SeparateMainQuestionnaire from "pages/separate-for-iledgends-main-ques";

import SeparateIndividualForm from "pages/separate-for-iledgends-individual/ind-form";
import SeparateIndividualDeclaration from "pages/separate-for-iledgends-individual/ind-declaration";
import SeparateIndividualDocuments from "pages/separate-for-iledgends-individual/ind-upload-docs";

import SeparateIndividual2Form from "pages/separate-for-iledgends-individual-2/ind-form";
import SeparateIndividual2Declaration from "pages/separate-for-iledgends-individual-2/ind-declaration";
import SeparateIndividual2Documents from "pages/separate-for-iledgends-individual-2/ind-upload-docs";

import SeparateCorporateInfo1 from "pages/separate-for-iledgends-corporate/form-corporate1";
import SeparateCorporateInfo2 from "pages/separate-for-iledgends-corporate/form-corporate2";
import SeparateCorporateInfo3 from "pages/separate-for-iledgends-corporate/form-corporate3";
import SeparateCorporateInfo4 from "pages/separate-for-iledgends-corporate/form-corporate4";
import SeparateCorporateInfo5 from "pages/separate-for-iledgends-corporate/form-corporate5";
import SeparateCorporateDeclaration from "pages/separate-for-iledgends-corporate/corp-declaration";
import SeparateEDDQuestionnaire from "pages/separate-for-iledgends-corporate/questionnaires-fb";
import SeparateCorporateUploadDocs from "pages/separate-for-iledgends-corporate/corp-upload-docs";
import Trade from "pages/trade";
import LogoutButton from "../components/logout-button";

const Login = lazy(() => import("pages/login"));
const Questionnaire = lazy(() => import("pages/questionnaire"));
const EDDQuestionnaire = lazy(() => import("pages/questionnaires-fb"));
const SignUp = lazy(() => import("pages/sign-up"));
const ResetPassword = lazy(() => import("pages/reset-password"));
const SetPassword = lazy(() => import("pages/set-password"));
const EmailConfirmed = lazy(() => import("pages/email-confirmed"));
const Dashboard = lazy(() => import("pages/dashboard"));
const Wallet = lazy(() => import("pages/wallet"));
const Transactions = lazy(() => import("pages/transactions"));
const Profile = lazy(() => import("pages/profile"));
const ProfileDocuments = lazy(() => import("pages/profile-documents"));
const CustomerDocuments = lazy(() => import("pages/customer-documents"));
const Toc = lazy(() => import("pages/toc"));
const Transfer = lazy(() => import("pages/transfer"));

const AppRouter: React.FC = () => {
  const isIledgends = useAppSelector((state) => state.app.isIledgends);
  const { data: user, isLoading } = useAccountQuery();

  if (isLoading) return <Loading />;

  if (
    isIledgends &&
    user?.result &&
    user.user.first_name === "" &&
    user.user.last_name === ""
  ) {
    return (
      <BrowserRouter>
        <div>
          <LogoutButton />
          <Routes>
            <Route
              path="/separate-main-ques"
              element={<SeparateMainQuestionnaire />}
            />
            <Route
              path="*"
              element={<Navigate to="/separate-main-ques" replace />}
            />
          </Routes>
        </div>
      </BrowserRouter>
    );
  }

  if (
    isIledgends &&
    user?.result &&
    user.user.wallet.wallet_type === "Private Wallet" &&
    user.user.kycProgram.enable_questionnaire_individual_lvl1 === true &&
    user.user.form1_json === null &&
    user.user.status_all !== "Related party"
  ) {
    return (
      <BrowserRouter>
        <div>
          <LogoutButton />
          <Routes>
            <Route
              path="/separate-form-first"
              element={<SeparateIndividualForm />}
            />
            <Route
              path="/separate-form-upload"
              element={<SeparateIndividualDocuments />}
            />
            <Route
              path="/separate-form-first-declaration"
              element={<SeparateIndividualDeclaration />}
            />
            <Route
              path="*"
              element={<Navigate to="/separate-form-first" replace />}
            />
          </Routes>
        </div>
      </BrowserRouter>
    );
  }

  if (
    isIledgends &&
    user?.result &&
    user.user.wallet.wallet_type === "Private Wallet" &&
    user.user?.questionnaire_level_2_enabled
  ) {
    return (
      <BrowserRouter>
        <div>
          <LogoutButton />
          <Routes>
            <Route
              path="/separate-form-first-2"
              element={<SeparateIndividual2Form />}
            />
            <Route
              path="/separate-form-upload-2"
              element={<SeparateIndividual2Documents />}
            />
            <Route
              path="/separate-form-first-declaration-2"
              element={<SeparateIndividual2Declaration />}
            />
            <Route
              path="*"
              element={<Navigate to="/separate-form-first-2" replace />}
            />
          </Routes>
        </div>
      </BrowserRouter>
    );
  }

  if (
    isIledgends &&
    user?.result &&
    user.user.wallet.wallet_type === "Business Wallet" &&
    user.user.kycProgram.enable_questionnaire_corporate_lvl1 === true &&
    user.user.form3_json === null
  ) {
    return (
      <BrowserRouter>
        <div>
          <LogoutButton />
          <Routes>
            <Route
              path="/separate-corp-info-1"
              element={<SeparateCorporateInfo1 />}
            />
            <Route
              path="/separate-corp-info-2"
              element={<SeparateCorporateInfo2 />}
            />
            <Route
              path="/separate-corp-info-3"
              element={<SeparateCorporateInfo3 />}
            />
            <Route
              path="/separate-corp-info-4"
              element={<SeparateCorporateInfo4 />}
            />
            <Route
              path="/separate-corp-info-5"
              element={<SeparateCorporateInfo5 />}
            />
            <Route
              path="/separate-corp-upload-docs"
              element={<SeparateCorporateUploadDocs />}
            />
            <Route
              path="/separate-corp-declaration"
              element={<SeparateCorporateDeclaration />}
            />
            <Route
              path="/separate-edd"
              element={<SeparateEDDQuestionnaire />}
            />
            <Route
              path="*"
              element={<Navigate to="/separate-corp-info-1" replace />}
            />
          </Routes>
        </div>
      </BrowserRouter>
    );
  }

  if (user?.result && user.user.is_blocked_except_forms && isIledgends) {
    return (
      <Suspense fallback={<Loading />}>
        <BrowserRouter>
          <Routes>
            <Route path="/profile" element={<Profile />} />
            <Route
              path="/separate-corp-info-1-view"
              element={<SeparateCorporateInfo1 />}
            />
            <Route
              path="/separate-corp-info-2"
              element={<SeparateCorporateInfo2 />}
            />
            <Route
              path="/separate-corp-info-3"
              element={<SeparateCorporateInfo3 />}
            />
            <Route
              path="/separate-corp-info-4"
              element={<SeparateCorporateInfo4 />}
            />
            <Route
              path="/separate-corp-info-5"
              element={<SeparateCorporateInfo5 />}
            />
            <Route
              path="/separate-corp-upload-docs"
              element={<SeparateCorporateUploadDocs />}
            />
            <Route
              path="/separate-corp-declaration-view"
              element={<SeparateCorporateDeclaration />}
            />
            <Route
              path="/separate-edd"
              element={<SeparateEDDQuestionnaire />}
            />
            <Route
              path="/separate-form-first-view"
              element={<SeparateIndividualForm />}
            />
            <Route
              path="/separate-form-upload"
              element={<SeparateIndividualDocuments />}
            />
            <Route
              path="/separate-form-first-declaration-view"
              element={<SeparateIndividualDeclaration />}
            />
            <Route path="/customer-documents" element={<CustomerDocuments />} />
            <Route
              path="/sum-sub-verification"
              element={<SumSubVerification />}
            />
            <Route path="*" element={<Navigate to="/profile" replace />} />
          </Routes>
        </BrowserRouter>
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<Loading />}>
      <BrowserRouter>
        <Routes>
          {!user?.result && (
            <>
              <Route path="/" element={<Login />} />
              <Route path="/signup" element={<SignUp />} />

              <Route path="/confirm-email" element={<ConfirmEmail />} />

              <Route
                path="/registration-completed"
                element={<SuccessRegistration />}
              />
              <Route path="/register" element={<SignUpWithReferral />} />
              <Route path="/form-first" element={<FormLevel1 />} />
              <Route
                path="/form-first-declaration"
                element={<FormLevel1Declaration />}
              />
              <Route path="/sign-up" element={<SignUpWithPassword />} />
              <Route
                path="/sign-up-iledgends"
                element={<SignUpWithPasswordIledgends />}
              />
              <Route path="/corp-info-1" element={<CorporateInfo1 />} />
              <Route path="/corp-info-2" element={<CorporateInfo2 />} />
              <Route path="/corp-info-3" element={<CorporateInfo3 />} />
              <Route path="/corp-info-4" element={<CorporateInfo4 />} />
              <Route path="/corp-info-5" element={<CorporateInfo5 />} />
              <Route path="/corp-info-6" element={<CorporateInfo6 />} />
              <Route path="/edd-ques" element={<EDDQuestionnaire />} />
              <Route
                path="/mobile-verification"
                element={<MobileVerification />}
              />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/set-password/:token" element={<SetPassword />} />
            </>
          )}
          {user?.result &&
            user.user.kycProgram.questionnaire_form_enabled === true &&
            user.user.questionnaire_answered === false && (
              <Route path="/" element={<Questionnaire />} />
            )}
          {user?.result &&
            user.user.kycProgram.questionnaire_form_enabled === false && (
              <>
                <Route path="/" element={<MainLayout />}>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/wallet" element={<Wallet />} />
                  <Route
                    path="/user-mobile-verification"
                    element={<UserMobileVerification />}
                  />
                  <Route path="/toc" element={<Toc />} />
                  <Route path="/transfer" element={<Transfer />} />
                  <Route path="/transactions" element={<Transactions />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route
                    path="/profile/documents"
                    element={<ProfileDocuments />}
                  />
                  <Route
                    path="/sum-sub-verification"
                    element={<SumSubVerification />}
                  />
                  <Route path="/uploader" element={<FileUploader />} />

                  <Route
                    path="/customer-documents"
                    element={<CustomerDocuments />}
                  />
                </Route>
                <Route path="/email_confirmed" element={<EmailConfirmed />} />
                <Route
                  path="/separate-corp-info-1-view"
                  element={<SeparateCorporateInfo1 />}
                />
                <Route
                  path="/separate-corp-info-2"
                  element={<SeparateCorporateInfo2 />}
                />
                <Route
                  path="/separate-corp-info-3"
                  element={<SeparateCorporateInfo3 />}
                />
                <Route
                  path="/separate-corp-info-4"
                  element={<SeparateCorporateInfo4 />}
                />
                <Route
                  path="/separate-corp-info-5"
                  element={<SeparateCorporateInfo5 />}
                />
                <Route
                  path="/separate-corp-upload-docs"
                  element={<SeparateCorporateUploadDocs />}
                />
                <Route
                  path="/separate-corp-declaration-view"
                  element={<SeparateCorporateDeclaration />}
                />
                <Route
                  path="/separate-edd"
                  element={<SeparateEDDQuestionnaire />}
                />
                <Route
                  path="/separate-form-first-view"
                  element={<SeparateIndividualForm />}
                />
                <Route
                  path="/separate-form-upload"
                  element={<SeparateIndividualDocuments />}
                />
                <Route
                  path="/separate-form-first-declaration-view"
                  element={<SeparateIndividualDeclaration />}
                />
              </>
            )}
          {user?.result &&
            user.user.kycProgram.questionnaire_form_enabled === true &&
            user.user.questionnaire_answered === true && (
              <>
                <Route
                  path="/separate-corp-info-1-view"
                  element={<SeparateCorporateInfo1 />}
                />
                <Route
                  path="/separate-corp-info-2"
                  element={<SeparateCorporateInfo2 />}
                />
                <Route
                  path="/separate-corp-info-3"
                  element={<SeparateCorporateInfo3 />}
                />
                <Route
                  path="/separate-corp-info-4"
                  element={<SeparateCorporateInfo4 />}
                />
                <Route
                  path="/separate-corp-info-5"
                  element={<SeparateCorporateInfo5 />}
                />
                <Route
                  path="/separate-corp-upload-docs"
                  element={<SeparateCorporateUploadDocs />}
                />
                <Route
                  path="/separate-corp-declaration-view"
                  element={<SeparateCorporateDeclaration />}
                />
                <Route
                  path="/separate-edd"
                  element={<SeparateEDDQuestionnaire />}
                />
                <Route
                  path="/separate-form-first-view"
                  element={<SeparateIndividualForm />}
                />
                <Route
                  path="/separate-form-upload"
                  element={<SeparateIndividualDocuments />}
                />
                <Route
                  path="/separate-form-first-declaration-view"
                  element={<SeparateIndividualDeclaration />}
                />
                <Route path="/" element={<MainLayout />}>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/wallet" element={<Wallet />} />
                  <Route path="/trade" element={<Trade />} />
                  <Route
                    path="/user-mobile-verification"
                    element={<UserMobileVerification />}
                  />
                  <Route path="/toc" element={<Toc />} />
                  <Route path="/transfer" element={<Transfer />} />
                  <Route path="/transactions" element={<Transactions />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/transaction" element={<Transaction />} />
                  <Route
                    path="/profile/documents"
                    element={<ProfileDocuments />}
                  />
                  <Route
                    path="/sum-sub-verification"
                    element={<SumSubVerification />}
                  />
                  <Route path="/uploader" element={<FileUploader />} />

                  <Route
                    path="/customer-documents"
                    element={<CustomerDocuments />}
                  />
                </Route>
                <Route path="/email_confirmed" element={<EmailConfirmed />} />
              </>
            )}
          {/* if !user.result then redirect with callbackUrl */}
          <Route
            path="*"
            element={
              <Navigate
                replace
                to={
                  !user?.result
                    ? `/?callbackUrl=${encodeURIComponent(
                        window.location.pathname + window.location.search
                      )}`
                    : "/"
                }
              />
            }
          />
        </Routes>
      </BrowserRouter>
    </Suspense>
  );
};

export default AppRouter;
